import {
  Alert,
  AlertTypography,
  Box,
  Icon,
  Link,
  Typography,
  List,
  ListItem,
} from '@ace-clublabs/shared-component-library';
import { IPartnerEnrollmentStatus, IPartnerIntegration, PartnerIntegration } from '@lib/types';
import { AlertColor } from '@mui/material/Alert/Alert';
import { IconName } from '@ace-clublabs/shared-component-library/dist/components/Icons';
import { useContext, useState } from 'react';
import { PartnerEnrollments } from '../../application/models/NewJoinSessionData';
import {
  getPartnerList,
  registrationLinkMap,
  linkageUrlMap,
  partnerName,
  getPartnerEnrollmentStatuses,
} from '../../utils/partnerEnrollmentHelper';
import { MembershipAppStateContext } from '../MembershipAppState/MembershipAppState';

type ListProps = {
  partnerIntegrations: IPartnerIntegration[];
};

const ParterErrorList = ({ partnerIntegrations }: ListProps) => {
  return (
    <List style={{ marginLeft: '0' }}>
      {partnerIntegrations.map((partner, index) => {
        const href = partner.hasExistingAccount ? linkageUrlMap[partner.name] : registrationLinkMap[partner.name];
        const linkText = partner.hasExistingAccount ? 'How to add it' : 'Register here';
        const partnerLink = (
          <Link target="_blank" href={href}>
            {linkText}
          </Link>
        );

        return (
          <ListItem key={index} sx={{ marginTop: '0.5rem' }}>
            <Typography component="span" sx={{ alignSelf: 'baseline', margin: '0 0.75rem' }}>
              {'\u2022'}
            </Typography>
            <Typography component="span">
              {partnerName[partner.name]} - {partnerLink}
            </Typography>
          </ListItem>
        );
      })}
    </List>
  );
};

export const PartnerEnrollmentStatusBanner = () => {
  const { sessionData } = useContext(MembershipAppStateContext);
  const partnerEnrollments = sessionData.formData.partnerEnrollments || ({} as PartnerEnrollments);

  const {
    failedPartnerLinkages,
    failedPartnerRegistrations,
    successfulPartnerLinkages,
    successfulPartnerRegistrations,
  } = getPartnerEnrollmentStatuses(sessionData);

  const someEnrollment = Object.values(partnerEnrollments).some((enrollment) => enrollment);

  const hasFailedEnrollments = failedPartnerLinkages.length + failedPartnerRegistrations.length > 0;

  const alertMessageRegistrationAndLonkages =
    failedPartnerRegistrations.length > 0 && failedPartnerLinkages.length > 0 ? (
      <Box>
        <Typography>We weren't able to complete your registration or add your AAA membership number to:</Typography>
        <ParterErrorList partnerIntegrations={[...failedPartnerRegistrations, ...failedPartnerLinkages]} />
      </Box>
    ) : (
      <></>
    );

  const alertMessageRegistrations =
    failedPartnerRegistrations.length > 0 && failedPartnerLinkages.length === 0 ? (
      <Box>
        <Typography>We weren't able to complete your registration for:</Typography>
        <ParterErrorList partnerIntegrations={failedPartnerRegistrations} />
      </Box>
    ) : (
      <></>
    );

  const alertMessageLinkages =
    failedPartnerLinkages.length > 0 && failedPartnerRegistrations.length === 0 ? (
      <Box>
        <Typography>We weren't able to add your AAA membership to:</Typography>
        <ParterErrorList partnerIntegrations={failedPartnerLinkages} />
      </Box>
    ) : (
      <></>
    );

  const successfulLinkageMessage =
    successfulPartnerLinkages.length > 0 ? (
      <Typography className="partner-banner-success-linkage" component={'span'}>
        Your AAA membership number has been added to your <strong>{getPartnerList(successfulPartnerLinkages)}</strong>{' '}
        account{successfulPartnerLinkages.length > 1 ? 's' : ''}.{' '}
      </Typography>
    ) : (
      <></>
    );

  const successfulRegistrationMessage =
    successfulPartnerRegistrations.length > 0 ? (
      <Typography className="partner-banner-success-registration" component={'span'}>
        Keep an eye out for an email from <strong>{getPartnerList(successfulPartnerRegistrations)}</strong>—it will
        guide you through the last steps to complete your enrollment.
      </Typography>
    ) : (
      <></>
    );

  if (!someEnrollment) {
    return <></>;
  }

  return (
    <Box className="partner-enrollment-status-banner-container" marginTop={'1rem'}>
      <Typography className="partner-success-banner-container" sx={{ marginBottom: '1rem' }}>
        {successfulLinkageMessage}
        {successfulRegistrationMessage}
      </Typography>
      {hasFailedEnrollments ? (
        <Alert
          className="partner-banner-alert"
          sx={{ marginBottom: '2rem', backgroundColor: '#FFEBE7' }}
          severity={'error'}
          icon={
            <Icon
              iconName={'triangle-exclamation-regular' as IconName}
              className={`partner-banner-triangle-exclamation-regular-icon`}
            />
          }
          displayCloseButton={false}
        >
          <AlertTypography className="partner-banner-alert-message">
            {alertMessageRegistrationAndLonkages}
            {alertMessageRegistrations}
            {alertMessageLinkages}
          </AlertTypography>
        </Alert>
      ) : (
        <></>
      )}
    </Box>
  );
};
